import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { describeMediaError, nextRate, recordingHost } from './recording';

/** The one recording playing on the page: starting another pauses it. */
let playing: HTMLAudioElement | null = null;

/** play() is a promise in browsers, nothing in jsdom; a refusal (autoplay blocked) leaves the controls to the admin. */
const tryPlay = (audio: HTMLAudioElement) => {
  try {
    const started = audio.play() as Promise<void> | undefined;
    started?.catch?.(() => undefined);
  } catch {
    // The error event says why, where there is a reason to say.
  }
};

export interface RecordingPlayerProps {
  url: string;
  /** Start at once: the admin just asked for it (a click), so browsers allow it. */
  autoPlay?: boolean;
  /**
   * 'none' (the default): nothing is downloaded until play, so a page of calls
   * costs nothing. 'metadata': fetch just enough to know it plays and how long
   * it is, for checking a link as it is entered.
   */
  preload?: 'none' | 'metadata';
  /** The recording's length, once the browser knows it. */
  onDuration?: (seconds: number) => void;
  /** Why it does not play, or null once it does. */
  onProblem?: (message: string | null) => void;
  /** The form shows its own problem text; the log and timeline show it here. */
  quietProblems?: boolean;
}

/**
 * A call's recording (card 21.7), played from where it is kept with the
 * browser's own controls: seek, volume and download come with them, and they
 * work with the keyboard and screen readers. Around them:
 * - **speed**, 1× to 2×, for going through calls quickly;
 * - **the link itself**, in a new tab, for when it plays better there or is
 *   to be saved;
 * - **a reason when it fails**, with "Try again", rather than a dead player;
 * - **one at a time**: starting a recording pauses any other on the page.
 */
export const RecordingPlayer = ({ url, autoPlay, preload = 'none', onDuration, onProblem, quietProblems }: RecordingPlayerProps) => {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const el = audio.current;
    if (el && autoPlay) {
      setLoading(true);
      tryPlay(el);
    }
  }, [autoPlay, url]);

  // Forgotten as the one playing only when it leaves the page: while it is here, starting
  // another must still find it to pause it.
  useEffect(() => {
    const el = audio.current;
    return () => {
      if (playing === el) playing = null;
    };
  }, []);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = rate;
  }, [rate]);

  const fail = () => {
    const message = describeMediaError(audio.current?.error?.code);
    setLoading(false);
    setProblem(message);
    onProblem?.(message);
  };

  const retry = () => {
    const el = audio.current;
    if (!el) return;
    setProblem(null);
    onProblem?.(null);
    setLoading(true);
    el.load();
    tryPlay(el);
  };

  return (
    // Clicks here are for the player, not the table row around it.
    <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'grid', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Box
          component="audio"
          ref={audio}
          controls
          preload={preload}
          src={url}
          aria-label={`Call recording from ${recordingHost(url) || 'its link'}`}
          onPlay={() => {
            if (playing && playing !== audio.current) playing.pause();
            playing = audio.current;
            if ((audio.current?.readyState ?? 0) < 3) setLoading(true);
          }}
          onWaiting={() => setLoading(true)}
          onPlaying={() => setLoading(false)}
          onPause={() => setLoading(false)}
          onCanPlay={() => setLoading(false)}
          onLoadedMetadata={() => {
            const seconds = audio.current?.duration;
            if (seconds && Number.isFinite(seconds)) onDuration?.(seconds);
            setProblem(null);
            onProblem?.(null);
          }}
          onError={fail}
          sx={{ height: 36, width: { xs: '100%', sm: 340 }, maxWidth: '100%' }}
        >
          Your browser cannot play recordings.
        </Box>
        {loading && !problem && <CircularProgress size={16} aria-label="Loading the recording" />}
        <Tooltip title="Playback speed">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setRate(nextRate(rate))}
            aria-label={`Playback speed ${rate} times. Change`}
            sx={{ minWidth: 48, px: 0.75, py: 0.25, fontVariantNumeric: 'tabular-nums', borderColor: '#dbe2ea', color: 'text.secondary' }}
          >
            {rate}×
          </Button>
        </Tooltip>
        <Tooltip title="Open the recording in a new tab">
          <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open the recording in a new tab">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {problem && !quietProblems && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ py: 0, maxWidth: 560 }}
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Try again
            </Button>
          }
        >
          {problem}{' '}
          <Link href={url} target="_blank" rel="noopener noreferrer">
            Open the link
          </Link>
        </Alert>
      )}
    </Box>
  );
};

/**
 * A recording that costs nothing until asked for: a button, then the player,
 * playing. For lists with many calls, such as a person's timeline.
 */
export const RecordingButton = ({ url }: { url: string }) => {
  const [open, setOpen] = useState(false);
  if (open) return <RecordingPlayer url={url} autoPlay />;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HeadphonesOutlinedIcon fontSize="small" />}
      onClick={(event) => {
        event.stopPropagation();
        setOpen(true);
      }}
      sx={{ textTransform: 'none', py: 0.25, borderColor: '#dbe2ea' }}
    >
      Play recording
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
        {recordingHost(url)}
      </Typography>
    </Button>
  );
};
